import React from "react";
import { Link } from "react-router-dom";
import { Building2, ShieldCheck } from "lucide-react";
import { useApp } from "../AppContext";
import { PROGERE_LEGAL } from "../config/progere-legal";

/**
 * ProgereLegalFooter — Footer universel Loi 25 pour l'écosystème PROGÈRE.
 * Affiché sur toutes les pages publiques et applicatives.
 * Ne contient AUCUNE couleur codée en dur : utilise les variables --pg-* du thème.
 */
export default function ProgereLegalFooter({ variant = "full", showLogin = false }) {
  const { lang } = useApp();
  const isFr = lang === "fr";
  const year = new Date().getFullYear();
  const cfg = PROGERE_LEGAL;

  return (
    <footer
      data-testid="progere-legal-footer"
      className="w-full mt-auto"
      style={{
        borderTop: "1px solid var(--pg-border)",
        background: "var(--pg-bg-secondary)",
        color: "var(--pg-text-secondary)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
          {/* Left — ecosystem */}
          <div className="flex items-start gap-3 max-w-md">
            <div
              className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{
                background: "color-mix(in srgb, var(--pg-gold) 12%, transparent)",
                border: "1px solid color-mix(in srgb, var(--pg-gold) 32%, transparent)",
              }}
            >
              <Building2 size={18} style={{ color: "var(--pg-gold)" }} />
            </div>
            <div>
              <div className="text-sm font-semibold" style={{ color: "var(--pg-text-primary)" }}>
                {cfg.entity.tradeName}
                <span className="mx-1.5" style={{ color: "var(--pg-text-muted)" }}>·</span>
                <span style={{ color: "var(--pg-gold)" }}>{cfg.entity.ecosystem}</span>
              </div>
              <div className="text-xs mt-1" style={{ color: "var(--pg-text-muted)" }}>
                {isFr ? "Propulsé par " : "Powered by "}
                <span className="font-semibold" style={{ color: "var(--pg-text-secondary)" }}>{cfg.entity.poweredBy}</span>
              </div>
              <div className="mt-3 flex items-center gap-2 text-[11px]" style={{ color: "var(--pg-text-muted)" }}>
                <ShieldCheck size={12} style={{ color: "var(--pg-success)" }} />
                <span>
                  {isFr
                    ? `Conforme Loi 25 (Québec) · Version ${cfg.versions.privacy}`
                    : `Compliant with Law 25 (Quebec) · Version ${cfg.versions.privacy}`}
                </span>
              </div>
            </div>
          </div>

          {/* Right — legal links */}
          <div className="flex-1 md:max-w-lg">
            <div className="text-[11px] uppercase tracking-widest mb-3 font-semibold" style={{ color: "var(--pg-text-muted)" }}>
              {isFr ? "Cadre légal" : "Legal framework"}
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
              {cfg.footerLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  data-testid={link.testId}
                  className="hover:underline"
                  style={{ color: "var(--pg-text-secondary)" }}
                >
                  {isFr ? link.labelFr : link.labelEn}
                </Link>
              ))}
              {showLogin && (
                <Link
                  to="/login"
                  data-testid="footer-login"
                  className="font-semibold hover:underline"
                  style={{ color: "var(--pg-gold)" }}
                >
                  {isFr ? "Se connecter" : "Sign in"}
                </Link>
              )}
            </div>
          </div>
        </div>

        {variant === "full" && (
          <div
            className="mt-6 pt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 text-[11px]"
            style={{ borderTop: "1px solid var(--pg-border)", color: "var(--pg-text-muted)" }}
          >
            <div>
              © {year} {cfg.entity.legalName} · {cfg.address.line1}
              <span className="mx-2">·</span>
              <a href={`mailto:${cfg.dpo.email}`} style={{ color: "var(--pg-gold)" }} data-testid="footer-dpo-email">
                {cfg.dpo.email}
              </a>
            </div>
            <div>
              {isFr ? "Autorité de contrôle : " : "Regulator: "}
              <a
                href={cfg.regulator.url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "var(--pg-text-secondary)" }}
                className="hover:underline"
                data-testid="footer-regulator"
              >
                {cfg.regulator.name}
              </a>
            </div>
          </div>
        )}
      </div>
    </footer>
  );
}
